import { Menu, LogOut, ShieldCheck, Bell } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ROLE_BADGE } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { NavList } from "./Nav";
import { Brand } from "./Sidebar";

export function Topbar() {
  const { user, logout } = useAuth();
  const initials = (user?.name || user?.email || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-white/80 px-4 backdrop-blur-md sm:px-6">
      <div className="flex items-center gap-3">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0">
            <div className="flex h-16 items-center border-b border-border px-5">
              <Brand />
            </div>
            <div className="py-6">
              <NavList />
            </div>
          </SheetContent>
        </Sheet>
        <div className="hidden items-center gap-2 text-sm text-brand-mute lg:flex">
          <ShieldCheck className="h-4 w-4 text-brand-blue" />
          <span>Human-in-the-loop · nothing sends without approval</span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <NotificationsBell />
        <span
          className={cn(
            "hidden rounded-full border px-2.5 py-1 text-xs font-semibold capitalize sm:inline-flex",
            ROLE_BADGE[user?.role] || ROLE_BADGE.observer
          )}
        >
          {user?.role_label || user?.role}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy font-heading text-sm font-bold text-white transition-transform hover:scale-105 active:scale-95"
            >
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="font-semibold text-brand-ink">{user?.name}</p>
              <p className="truncate text-xs font-normal text-brand-mute">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="logout-btn"
              onClick={logout}
              className="cursor-pointer text-brand-red focus:text-brand-red"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

function NotificationsBell() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications").then((r) => r.data),
    refetchInterval: 60000,
  });
  const markRead = async () => {
    if (data?.unread) {
      await api.post("/notifications/mark-read");
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
  };
  return (
    <DropdownMenu onOpenChange={(open) => open && markRead()}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white text-brand-ink transition-colors hover:bg-slate-50"
        >
          <Bell className="h-4 w-4" />
          {data?.unread > 0 && (
            <span data-testid="notifications-unread-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-red px-1 text-[0.6rem] font-bold text-white">
              {data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notifications-panel">
        <DropdownMenuLabel>Alerts</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data?.items?.length ? (
          data.items.slice(0, 8).map((n) => (
            <div key={n.id} className="px-2 py-2">
              <p className="text-xs font-medium text-brand-ink">{n.message}</p>
              <p className="mt-0.5 text-[0.65rem] capitalize text-brand-mute">{n.type} · {timeAgo(n.created_at)}</p>
            </div>
          ))
        ) : (
          <p className="px-2 py-4 text-center text-xs text-brand-mute">No alerts yet, spikes and trigger hits land here.</p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
